import {
  AppShell,
  Avatar,
  Badge,
  Burger,
  Button,
  Group,
  Menu,
  NavLink,
  Stack,
  Switch,
  Text,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { type ComponentType, useEffect, useState } from "react";
import { NavLink as RouterLink, Outlet, useLocation } from "react-router";

import type { SiteOut, StatusOut } from "../api/generated/model";
import { useAbort, useRequestSweep, useTrigger } from "../api/generated/stockvol";
import { useOpenEvents, useSiteInfo, useStatusPoll, useVersionInfo } from "../api/hooks";
import { atLeast, RequireRole, type Role, useAuth } from "../auth";
import { StatusDot } from "../components/bits";
import {
  IconBell,
  IconChartLine,
  IconChevronDown,
  IconClipboardList,
  IconClock,
  IconCube,
  IconFileSpreadsheet,
  IconLayoutDashboard,
  IconListDetails,
  IconLogout,
  IconPlayerPlay,
  IconPlayerStop,
  IconRadar2,
  IconSend,
  IconSettings,
} from "../components/icons";
import { Logo } from "../components/Logo";
import { act, confirm } from "../lib/actions";
import { setSoundEnabled, soundEnabled, sweepState } from "../lib/prompt";
import { formatAge, formatLocal } from "../lib/time";
import { palette } from "../palette";
import { Banners } from "./Banners";

type Icon = ComponentType<{ size?: number; stroke?: number }>;

export const NAV: { to: string; label: string; min: Role; icon: Icon }[] = [
  { to: "/", label: "Overview", min: "viewer", icon: IconLayoutDashboard },
  { to: "/dem", label: "3D view", min: "viewer", icon: IconCube },
  { to: "/trends", label: "Trends", min: "viewer", icon: IconChartLine },
  { to: "/runs", label: "Runs", min: "viewer", icon: IconListDetails },
  { to: "/reports", label: "Reports", min: "viewer", icon: IconFileSpreadsheet },
  { to: "/events", label: "Events", min: "viewer", icon: IconBell },
  { to: "/operations", label: "Operations log", min: "operator", icon: IconClipboardList },
  { to: "/admin", label: "Admin", min: "admin", icon: IconSettings },
];

const FRESH = { ok: "teal", due: "yellow", overdue: "red", unknown: "gray" } as const;

export function Shell() {
  const { me } = useAuth();
  const site = useSiteInfo();
  const status = useStatusPoll();
  const events = useOpenEvents();
  const [sound, setSound] = useState(soundEnabled);
  const [opened, { toggle, close }] = useDisclosure(false);
  const location = useLocation();
  const open = events.data?.items.length ?? 0;
  const alarms = events.data?.items.filter((e) => e.severity === "alarm").length ?? 0;

  useEffect(close, [location.pathname, close]); // a phone: the menu closes on navigation

  return (
    <AppShell
      layout="alt"
      header={{ height: 60 }}
      navbar={{ width: 240, breakpoint: "sm", collapsed: { mobile: !opened } }}
      padding="lg"
    >
      <AppShell.Header className="sv-topbar">
        <TopBar
          site={site.data}
          status={status.data}
          alarms={alarms}
          opened={opened}
          onBurger={toggle}
        />
      </AppShell.Header>
      <AppShell.Navbar className="sv-sidebar" p="md">
        <AppShell.Section>
          <Group justify="space-between" wrap="nowrap" mb="lg" mt={4}>
            <Logo tagline="Stockpile monitoring" />
            <Burger hiddenFrom="sm" size="sm" color="#fff" opened={opened} onClick={toggle} aria-label="Close navigation" />
          </Group>
        </AppShell.Section>
        <AppShell.Section grow>
          {NAV.filter((n) => atLeast(me?.role, n.min)).map((n) => {
            const Ico = n.icon;
            return (
              <NavLink
                key={n.to}
                className="sv-nav"
                component={RouterLink}
                to={n.to}
                leftSection={<Ico size={18} stroke={1.7} />}
                label={
                  n.to === "/events" && open > 0 ? (
                    <Group gap={8} wrap="nowrap" justify="space-between">
                      {n.label}
                      <Badge size="sm" circle variant="filled" color={alarms > 0 ? "red" : "yellow"} data-testid="events-badge">
                        {open}
                      </Badge>
                    </Group>
                  ) : (
                    n.label
                  )
                }
                active={n.to === "/" ? location.pathname === "/" : location.pathname.startsWith(n.to)}
              />
            );
          })}
        </AppShell.Section>
        <AppShell.Section>
          <SystemStatus status={status.data} ok={status.isSuccess} />
          <Switch
            mt="md"
            size="xs"
            label="Sound"
            checked={sound}
            onChange={(e) => {
              setSound(e.currentTarget.checked);
              setSoundEnabled(e.currentTarget.checked);
            }}
            styles={{ label: { color: palette.side.text } }}
          />
          <UserBlock />
        </AppShell.Section>
      </AppShell.Navbar>
      <AppShell.Main style={{ background: "var(--sv-canvas)" }}>
        <div style={{ maxWidth: 1600, margin: "0 auto" }}>
          <Banners status={status.data} site={site.data} sound={sound} />
          <Outlet />
        </div>
      </AppShell.Main>
    </AppShell>
  );
}

function TopBar({
  site,
  status,
  alarms,
  opened,
  onBurger,
}: {
  site: SiteOut | undefined;
  status: StatusOut | undefined;
  alarms: number;
  opened: boolean;
  onBurger: () => void;
}) {
  const request = useRequestSweep();
  const [requested, setRequested] = useState<string | null>(null);
  const tz = site?.timezone ?? "UTC";
  const state = status && site ? sweepState(status.last_complete_sweep_age_s, site.sweep) : "unknown";
  const recording = Boolean(status?.ingest?.["recording"]);
  return (
    <Group h="100%" px="lg" justify="space-between" wrap="nowrap" gap="md">
      <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
        <Burger hiddenFrom="sm" size="sm" opened={opened} onClick={onBurger} aria-label="Toggle navigation" />
        <Stack gap={0} style={{ minWidth: 0 }}>
          <Text fw={600} size="sm" truncate>
            {site?.name ?? ""}
          </Text>
          <Text size="xs" c="dimmed" truncate>
            {site ? `Stacker ${site.stacker_code} · ${site.timezone}` : ""}
          </Text>
        </Stack>
      </Group>
      <Group gap="sm" wrap="nowrap">
        {recording && (
          <Badge color="red" variant="light" leftSection={<span className="sv-pulse" />} visibleFrom="xs">
            Recording
          </Badge>
        )}
        {status && (
          <Tooltip
            label={
              status.last_complete_sweep
                ? `Last complete sweep ended ${formatLocal(status.last_complete_sweep, tz)}`
                : "No complete sweep yet"
            }
          >
            <Badge color={FRESH[state]} variant="light" tt="none" size="lg" fw={500} visibleFrom="md">
              {status.last_complete_sweep_age_s != null
                ? `Last sweep · ${formatAge(status.last_complete_sweep_age_s)} ago`
                : "No sweep yet"}
            </Badge>
          </Tooltip>
        )}
        {alarms > 0 && (
          <Badge component={RouterLink} to="/events" color="red" variant="filled" tt="none" size="lg" style={{ cursor: "pointer" }}>
            {alarms} {alarms === 1 ? "alarm" : "alarms"}
          </Badge>
        )}
        <RequireRole min="operator">
          <SweepMenu onRequested={setRequested} request={request} />
          {requested && (
            <Text size="xs" c="teal">
              {requested}
            </Text>
          )}
        </RequireRole>
        <Group gap={6} wrap="nowrap" visibleFrom="xs">
          <IconClock size={16} color={palette.muted} />
          <Text size="sm" c="dimmed" className="sv-num" data-testid="clock">
            {status ? formatLocal(status.t, tz) : ""}
          </Text>
        </Group>
      </Group>
    </Group>
  );
}

/** The services behind the screen: ingest, processing, the database, the disk. */
function SystemStatus({ status, ok }: { status: StatusOut | undefined; ok: boolean }) {
  const age = (svc: string): number | null => {
    const hb = status?.heartbeats?.[svc];
    if (!status || typeof hb !== "string") return null;
    return (new Date(status.t).getTime() - new Date(hb).getTime()) / 1000;
  };
  const tone = (s: number | null) => (s === null ? "off" : s < 90 ? "ok" : s < 300 ? "warning" : "alarm");
  const note = (s: number | null, fine: string) =>
    s === null ? "no signal" : s < 90 ? fine : s < 300 ? "delayed" : "not reporting";
  const disk = status?.disk?.["usage_frac"];
  const diskFrac = typeof disk === "number" ? disk : null;
  const rows: { label: string; tone: "ok" | "warning" | "alarm" | "off"; note: string }[] = [
    { label: "Sensor feed", tone: tone(age("ingest")), note: note(age("ingest"), "live") },
    { label: "Processing", tone: tone(age("worker")), note: note(age("worker"), "running") },
    { label: "Database", tone: !ok ? "off" : status?.db ? "ok" : "alarm", note: status?.db ? "online" : "offline" },
    {
      label: "Disk",
      tone: diskFrac === null ? "off" : diskFrac > 0.9 ? "alarm" : diskFrac > 0.8 ? "warning" : "ok",
      note: diskFrac === null ? "—" : `${Math.round(diskFrac * 100)} % used`,
    },
  ];
  return (
    <Stack gap={6} pt="md" className="sv-side-sep">
      <Text className="sv-eyebrow" style={{ color: palette.side.muted }}>
        System
      </Text>
      {rows.map((r) => (
        <Group key={r.label} justify="space-between" wrap="nowrap">
          <Group gap={8} wrap="nowrap">
            <StatusDot tone={r.tone} label={`${r.label}: ${r.note}`} />
            <Text size="xs" c={palette.side.text}>
              {r.label}
            </Text>
          </Group>
          <Text size="xs" className="sv-side-muted">
            {r.note}
          </Text>
        </Group>
      ))}
    </Stack>
  );
}

function UserBlock() {
  const { me, logout } = useAuth();
  const version = useVersionInfo();
  const v = version.data;
  return (
    <Stack gap={8} mt="md" pt="md" className="sv-side-sep">
      <Group justify="space-between" wrap="nowrap">
        <Group gap={10} wrap="nowrap" style={{ minWidth: 0 }}>
          <Avatar size={32} radius="xl" color="brand" variant="filled">
            {(me?.username ?? "?").slice(0, 2).toUpperCase()}
          </Avatar>
          <Stack gap={0} style={{ minWidth: 0 }}>
            <Text size="sm" fw={600} c="#fff" truncate>
              {me?.username}
            </Text>
            <Badge size="xs" variant="outline" color="gray.4" w="fit-content">
              {me?.role}
            </Badge>
          </Stack>
        </Group>
        <Button
          size="xs"
          variant="subtle"
          color="gray.4"
          leftSection={<IconLogout size={14} />}
          onClick={() => void logout()}
        >
          Log out
        </Button>
      </Group>
      {v?.version && (
        <Text fz={11} className="sv-side-muted">
          StockVol {v.version}
          {v.git_sha ? ` · ${v.git_sha.slice(0, 7)}` : ""}
        </Text>
      )}
    </Stack>
  );
}

/** "Sweep" for operators: ask the driver for one (an event), or start / stop a recording at
 * once (ingest commands, each confirmed). */
function SweepMenu({
  onRequested,
  request,
}: {
  onRequested: (s: string) => void;
  request: ReturnType<typeof useRequestSweep>;
}) {
  const qc = useQueryClient();
  const trigger = useTrigger();
  const abort = useAbort();
  return (
    <Menu shadow="md" width={250} position="bottom-end">
      <Menu.Target>
        <Button
          size="sm"
          leftSection={<IconRadar2 size={16} />}
          rightSection={<IconChevronDown size={14} />}
          loading={request.isPending || trigger.isPending || abort.isPending}
        >
          Sweep
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          leftSection={<IconSend size={15} />}
          onClick={() =>
            request.mutate({ data: { note: null } }, { onSuccess: () => onRequested("Sweep requested") })
          }
        >
          Request sweep
        </Menu.Item>
        <Menu.Item
          leftSection={<IconPlayerPlay size={15} />}
          onClick={() =>
            confirm("Start recording", "Start recording a sweep now (the stacker should be at an arc end)?", () =>
              void act(() => trigger.mutateAsync({ data: {} }), { done: "Recording started", qc, refresh: ["status"] }),
            )
          }
        >
          Start recording now
        </Menu.Item>
        <Menu.Item
          color="red"
          leftSection={<IconPlayerStop size={15} />}
          onClick={() =>
            confirm(
              "Stop recording",
              "Stop the recording in progress? It is kept, cut short.",
              () => void act(() => abort.mutateAsync({ data: {} }), { done: "Recording stopped", qc, refresh: ["status"] }),
              "Yes",
              true,
            )
          }
        >
          Stop recording
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
